import designStyles from '../styles/design-form-styles.module.css';
import { useEffect, useMemo, useState } from 'react';
import { Cast, DefaultCastStyle } from '@he-novation/config/types/cast.types';
import { Project } from '@he-novation/config/types/project.types';
import { UserFile, UserFileType } from '@he-novation/config/types/user-file.types';
import { colorAverage } from '@he-novation/config/utils/colors';
import { FormField } from '@he-novation/design-system/components/form/FormField/FormField';
import { asyncWorkspaceUserFilesFetch } from '@he-novation/front-shared/async/workspaceUserFile.async';
import cn from 'classnames';
import { useAtomValue } from 'jotai';
import { PreviewData, SetPreviewData } from './useFormCast';

import { workspaceAtom, workspaceNameAtom } from '$atoms/workspace-atoms';
import { UserFilePicker } from '$components/form/fields/UserFilePicker/UserFilePicker';
import { CastPreview } from '$components/form/FormCast/CastPreview/CastPreview';
import { useTranslate } from '$hooks/useTranslate';

type CastUserFile = Omit<UserFile, 'isPrivate'> | undefined | null;

type FormCastDesignProps = {
    previewData: PreviewData;
    setPreviewData: SetPreviewData;
    cast?: Partial<Cast>;
    setLogoUserFile: (value: CastUserFile | null) => void;
    setBackgroundUserFile: (value: CastUserFile | undefined | null) => void;
    setBackgroundColor: (value: string | null) => void;
    projectStyle?: Pick<Project, 'logoUrl' | 'backgroundUrl' | 'color'>;
    className?: string;
};

export function FormCastDesign({
    previewData,
    setPreviewData,
    cast,
    setLogoUserFile,
    setBackgroundUserFile,
    setBackgroundColor,
    projectStyle,
    className
}: FormCastDesignProps) {
    const [logoUserFiles, setLogoUserFiles] = useState<UserFile[]>([]);
    const [backgroundUserFiles, setBackgroundUserFiles] = useState<UserFile[]>([]);

    const workspaceName = useAtomValue(workspaceNameAtom);
    const workspace = useAtomValue(workspaceAtom)!;

    useEffect(() => {
        Promise.all([
            asyncWorkspaceUserFilesFetch(workspaceName, UserFileType.LOGO),
            asyncWorkspaceUserFilesFetch(workspaceName, UserFileType.CLIENT_LOGO)
        ]).then((logoUserFiles) => {
            setLogoUserFiles(logoUserFiles.flat());
        });

        Promise.all([
            asyncWorkspaceUserFilesFetch(workspaceName, UserFileType.BACKGROUND),
            asyncWorkspaceUserFilesFetch(workspaceName, UserFileType.CLIENT_BACKGROUND)
        ]).then((backgroundUserFiles) => {
            setBackgroundUserFiles(backgroundUserFiles.flat());
        });
    }, []);

    const getTextColor = (backgroundColor: string) => {
        return colorAverage(backgroundColor) < 127 ? '#ffffff' : '#000000';
    };

    const castStyle: DefaultCastStyle = useMemo(() => {
        if (cast) {
            return {
                logoUserFileUid: cast.logoUserFile?.uid,
                backgroundUserFileUid: cast.bgUserFile?.uid,
                backgroundColor: cast.backgroundColor,
                textColor: cast.textColor
            };
        }

        if (projectStyle) {
            const projectLogoUserFileUid = projectStyle?.logoUrl
                ? logoUserFiles.find((f) => f.url === projectStyle.logoUrl)?.uid
                : undefined;
            const projectBackgroundUserFileUid = projectStyle?.backgroundUrl
                ? backgroundUserFiles.find((f) => f.url === projectStyle.backgroundUrl)?.uid
                : undefined;

            return {
                logoUserFileUid: projectLogoUserFileUid,
                backgroundUserFileUid: projectBackgroundUserFileUid,
                backgroundColor: projectStyle.color
            };
        }

        return {
            logoUserFileUid: workspace.defaultCastStyle?.logoUserFileUid,
            backgroundUserFileUid: workspace.defaultCastStyle?.backgroundUserFileUid,
            backgroundColor: workspace.defaultCastStyle?.backgroundColor,
            textColor: getTextColor(workspace.defaultCastStyle?.backgroundColor || '#0d111a')
        };
    }, [logoUserFiles, backgroundUserFiles, cast, projectStyle, workspace]);

    const updatePreviewDataParam = (param: keyof PreviewData, value?: UserFile | string | null) =>
        setPreviewData((prev) => ({
            ...prev,
            [param]: value || null
        }));

    useEffect(() => {
        updatePreviewDataParam(
            'logoUserFile',
            logoUserFiles.find(({ uid }) => uid === castStyle.logoUserFileUid)
        );
        updatePreviewDataParam(
            'bgUserFile',
            backgroundUserFiles.find(({ uid }) => uid === castStyle.backgroundUserFileUid)
        );
        updatePreviewDataParam('backgroundColor', castStyle.backgroundColor);
        updatePreviewDataParam(
            'textColor',
            castStyle.textColor || getTextColor(castStyle.backgroundColor || '#0d111a')
        );
    }, [castStyle]);

    const { t } = useTranslate();

    return (
        <div className={cn(designStyles.designForm, className)}>
            <CastPreview
                className={cn(designStyles.preview)}
                castName={previewData.castName}
                backgroundColor={previewData.backgroundColor}
                bgUserFile={previewData.bgUserFile}
                logoUserFile={previewData.logoUserFile}
                textColor={previewData.textColor}
            />

            <div className={designStyles.pickers}>
                <UserFilePicker
                    className={designStyles.logo}
                    name="logoImage"
                    formId="form-cast"
                    label={t('misc.Logo')}
                    type={UserFileType.LOGO}
                    value={logoUserFiles.find(({ uid }) => uid === castStyle.logoUserFileUid)}
                    values={logoUserFiles}
                    onAdd={(userFile: UserFile) => {
                        setLogoUserFiles([...logoUserFiles, userFile]);
                        setLogoUserFile(userFile);
                        updatePreviewDataParam('logoUserFile', userFile);
                    }}
                    onChange={(userFile: UserFile | null) => {
                        setLogoUserFile(userFile);
                        updatePreviewDataParam('logoUserFile', userFile);
                    }}
                    onRemove={(uid: string) => {
                        setLogoUserFiles(logoUserFiles.filter((f) => f.uid !== uid));
                    }}
                />

                <UserFilePicker
                    className={designStyles.background}
                    name="backgroundImage"
                    formId="form-cast"
                    label={t('misc.Background')}
                    type={UserFileType.BACKGROUND}
                    value={backgroundUserFiles.find(
                        ({ uid }) => uid === castStyle.backgroundUserFileUid
                    )}
                    values={backgroundUserFiles}
                    onAdd={(userFile: UserFile) => {
                        setBackgroundUserFiles([...backgroundUserFiles, userFile]);
                    }}
                    onChange={(userFile: UserFile | null) => {
                        setBackgroundUserFile(userFile);
                        updatePreviewDataParam('bgUserFile', userFile);
                    }}
                    onRemove={(uid: string) => {
                        setBackgroundUserFiles(backgroundUserFiles.filter((f) => f.uid !== uid));
                    }}
                    noTopMargin={true}
                />

                <FormField
                    formId="form-cast"
                    name="backgroundColor"
                    label={t('common.Color')}
                    type="color-picker"
                    value={castStyle.backgroundColor}
                    onChange={(e) => {
                        const value = e.target.value;
                        setBackgroundColor(value);
                        updatePreviewDataParam('backgroundColor', value);
                        updatePreviewDataParam('textColor', getTextColor(value));
                    }}
                    noTopMargin={true}
                />
            </div>
        </div>
    );
}
